import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import PhoneInput, { formatPhoneNumber, parsePhoneNumber } from '@/components/PhoneInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Users, Plus, Search, Edit, Trash2, UserPlus, 
  Building2, Hotel, CheckCircle, XCircle, MoreVertical,
  Key, Power, PowerOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function CompanyUsers({ user: userProp, isTab, tabId, tabData }) {
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'hotel_admin',
    hotel_id: '',
    phone: ''
  });

  // Get user from prop or localStorage (for tab-based rendering)
  const user = userProp || (isTab ? JSON.parse(localStorage.getItem('user') || '{}') : null);
  
  // Get company_id from user's company_ids array or company_id
  const companyId = user?.company_ids?.[0] || user?.company_id;

  const fetchUsers = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const params = roleFilter !== 'all' ? `?role=${roleFilter}` : '';
      const response = await api.get(`/companies/${companyId}/users${params}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [companyId, roleFilter]);

  const fetchHotels = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await api.get(`/companies/${companyId}/hotels`);
      setHotels(response.data);
    } catch (error) {
      console.error('Failed to fetch hotels:', error);
    }
  }, [companyId]);

  const fetchCustomRoles = useCallback(async () => {
    try {
      const response = await api.get('/roles');
      setCustomRoles(response.data.custom_roles || []);
    } catch (error) {
      console.error('Failed to fetch custom roles:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchHotels();
    fetchCustomRoles();
  }, [fetchUsers, fetchHotels, fetchCustomRoles]);

  // Get available roles based on current user's role
  const getAvailableRoles = () => {
    const baseRoles = [
      { value: 'manager', label: 'Manager' },
      { value: 'front_office', label: 'Front Office' },
      { value: 'service_staff', label: 'Service Staff' },
      { value: 'housekeeping', label: 'Housekeeping' },
      { value: 'restaurant_staff', label: 'Restaurant Staff' }
    ];
    
    // Hotel Admin cannot create Hotel Admin, Company Admin, Finance Admin, or Super Admin
    if (user?.role === 'hotel_admin') {
      // Add custom roles
      const roles = [...baseRoles];
      customRoles.forEach(cr => {
        if (cr.is_active !== false) {
          roles.push({ value: cr.id, label: cr.name, isCustom: true });
        }
      });
      return roles;
    }
    
    // Company Admin and Super Admin can create all roles
    const allRoles = [
      { value: 'company_admin', label: 'Company Admin' },
      { value: 'finance_admin', label: 'Finance Admin' },
      { value: 'hotel_admin', label: 'Hotel Admin' },
      ...baseRoles
    ];
    
    // Add custom roles
    customRoles.forEach(cr => {
      if (cr.is_active !== false) {
        allRoles.push({ value: cr.id, label: cr.name, isCustom: true });
      }
    });
    
    return allRoles;
  };

  const handleCreate = async () => {
    try {
      if (!formData.name || !formData.email || !formData.password) {
        toast.error('Name, Email and Password are required');
        return;
      }
      
      await api.post(`/companies/${companyId}/users`, formData);
      toast.success('User created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleEdit = async () => {
    try {
      if (!formData.name || !formData.email) {
        toast.error('Name and Email are required');
        return;
      }
      
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        hotel_id: formData.hotel_id === 'none' ? null : (formData.hotel_id || null),
        phone: formData.phone || null
      };
      
      await api.put(`/companies/${companyId}/users/${selectedUser.id}`, updateData);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      setSelectedUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/companies/${companyId}/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const handleToggleStatus = async (targetUser) => {
    try {
      const response = await api.patch(`/companies/${companyId}/users/${targetUser.id}/toggle-status`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to toggle user status'));
    }
  };

  const handleResetPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      
      await api.post(`/companies/${companyId}/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setShowResetPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset password'));
    }
  };

  const openEditDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setFormData({
      name: targetUser.name,
      email: targetUser.email,
      password: '',
      role: targetUser.role,
      hotel_id: targetUser.hotel_id || '',
      phone: targetUser.phone || ''
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setShowDeleteDialog(true);
  };

  const openResetPasswordDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setNewPassword('');
    setShowResetPasswordDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'hotel_admin',
      hotel_id: '',
      phone: ''
    });
  };

  const getRoleBadge = (role) => {
    const variants = {
      company_admin: { className: 'bg-purple-100 text-purple-700', label: 'Company Admin' },
      finance_admin: { className: 'bg-green-100 text-green-700', label: 'Finance Admin' },
      hotel_admin: { className: 'bg-blue-100 text-blue-700', label: 'Hotel Admin' },
      manager: { className: 'bg-orange-100 text-orange-700', label: 'Manager' },
      front_office: { className: 'bg-yellow-100 text-yellow-700', label: 'Front Office' },
      service_staff: { className: 'bg-teal-100 text-teal-700', label: 'Service Staff' },
      housekeeping: { className: 'bg-cyan-100 text-cyan-700', label: 'Housekeeping' },
      restaurant_staff: { className: 'bg-pink-100 text-pink-700', label: 'Restaurant' }
    };
    const config = variants[role] || { className: 'bg-gray-100 text-gray-700', label: role };
    
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if current user can manage the target user
  const canManageUser = (targetUser) => {
    // Cannot manage yourself
    if (targetUser.id === user?.id) return false;
    return true;
  };

  return (
    <div className="space-y-6" data-testid="company-users">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-8 w-8 text-orange-500" />
            Company Users
          </h1>
          <p className="text-gray-600 mt-1">Manage users across your hotels</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          data-testid="create-user-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
              <Users className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Company Admins</p>
                <p className="text-2xl font-bold text-purple-600">
                  {users.filter(u => u.role === 'company_admin').length}
                </p>
              </div>
              <Building2 className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Finance Admins</p>
                <p className="text-2xl font-bold text-green-600">
                  {users.filter(u => u.role === 'finance_admin').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Hotel Users</p>
                <p className="text-2xl font-bold text-blue-600">
                  {users.filter(u => ['hotel_admin', 'manager', 'front_office', 'service_staff', 'housekeeping', 'restaurant_staff'].includes(u.role)).length}
                </p>
              </div>
              <Hotel className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="company_admin">Company Admin</SelectItem>
                <SelectItem value="finance_admin">Finance Admin</SelectItem>
                <SelectItem value="hotel_admin">Hotel Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="front_office">Front Office</SelectItem>
                <SelectItem value="service_staff">Service Staff</SelectItem>
                <SelectItem value="housekeeping">Housekeeping</SelectItem>
                <SelectItem value="restaurant_staff">Restaurant Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Hotel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2">Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <p className="font-medium">{u.name}</p>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{getRoleBadge(u.role)}</TableCell>
                    <TableCell>
                      {u.hotel_id ? (
                        <Badge variant="outline">
                          <Hotel className="h-3 w-3 mr-1" />
                          {hotels.find(h => h.id === u.hotel_id)?.name || 'Unknown'}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">Company-wide</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.is_active !== false ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManageUser(u) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(u)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openResetPasswordDialog(u)}>
                              <Key className="h-4 w-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggleStatus(u)}>
                              {u.is_active !== false ? (
                                <>
                                  <PowerOff className="h-4 w-4 mr-2 text-orange-500" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Power className="h-4 w-4 mr-2 text-green-500" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => openDeleteDialog(u)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to your company
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <PhoneInput
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
                data-testid="user-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableRoles().map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label} {role.isCustom && <span className="text-xs text-gray-400">(Custom)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {['hotel_admin', 'manager', 'front_office', 'restaurant_staff'].includes(formData.role) && (
              <div>
                <Label htmlFor="hotel">Assign to Hotel</Label>
                <Select 
                  value={formData.hotel_id} 
                  onValueChange={(value) => setFormData({ ...formData, hotel_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    {hotels.map((hotel) => (
                      <SelectItem key={hotel.id} value={hotel.id}>
                        {hotel.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="save-user-btn">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone</Label>
              <PhoneInput
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
                data-testid="edit-user-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableRoles().map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label} {role.isCustom && <span className="text-xs text-gray-400">(Custom)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {['hotel_admin', 'manager', 'front_office', 'restaurant_staff'].includes(formData.role) && (
              <div>
                <Label htmlFor="edit-hotel">Assign to Hotel</Label>
                <Select 
                  value={formData.hotel_id} 
                  onValueChange={(value) => setFormData({ ...formData, hotel_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Company-wide (No specific hotel)</SelectItem>
                    {hotels.map((hotel) => (
                      <SelectItem key={hotel.id} value={hotel.id}>
                        {hotel.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEdit} data-testid="update-user-btn">Update User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="new-password">New Password *</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleResetPassword}>Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
